// 文章正文渲染(详情页)。遍历 ProseMirror JSON:
//   - 文本 / 标题 / 列表 / 引用 / link 直接渲染
//   - fileRef(只存 fileId)→ 收集全部 id → rpc get_public_files 批量解析 → 文件卡 → /preview/:id
//   - packRef(只存 packId)→ v_resource_pack_public_meta 批量解析 → 资源包卡 → /pack/:id
//   - 解析进行中(网速慢)→「资源正在加载中…」占位(不可误判为失效)
//   - 解析完成仍缺失(文件非公开/已删/folder 被过滤;包 cancelled)→ 优雅降级「内容已失效」
//
// D10:嵌入只存 uuid,元数据/缩略图/链接全部运行时由本组件生成。

import { Fragment, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { JSONContent } from '@tiptap/core'
import { formatBytes } from '@dogdisk/shared'
import { collectEmbedIds } from '../article-editor/embed-utils'
import {
  useResolvePublicFiles,
  useResolvePublicPacks,
  type PublicFileMeta,
  type PublicPackMeta,
} from '../../_lib/article-api'
import { thumbUrl } from '../../_lib/thumb-url'
import { streamUrlOf } from '../file-view/use-file-meta'

type ArticleRenderProps = {
  content: JSONContent | null | undefined
}

/** 嵌入解析上下文:已命中的 file/pack 元数据 + 各自解析是否仍在进行中。 */
type EmbedCtx = {
  fileMap: Map<string, PublicFileMeta>
  packMap: Map<string, PublicPackMeta>
  filesLoading: boolean
  packsLoading: boolean
}

export function ArticleRender({ content }: ArticleRenderProps) {
  const { fileIds, packIds } = collectEmbedIds(content)
  const filesQ = useResolvePublicFiles(fileIds)
  const packsQ = useResolvePublicPacks(packIds)

  const fileMap = new Map<string, PublicFileMeta>()
  for (const f of filesQ.data ?? []) fileMap.set(f.id, f)
  const packMap = new Map<string, PublicPackMeta>()
  for (const p of packsQ.data ?? []) packMap.set(p.id, p)

  // 仍在拉取(尚无 data)→ 嵌入卡片显示加载占位,而非误判「已失效」。
  const ctx: EmbedCtx = {
    fileMap,
    packMap,
    filesLoading: filesQ.isLoading,
    packsLoading: packsQ.isLoading,
  }

  if (!content || !content.content) return null

  return (
    <div className="prose prose-sm max-w-none text-[#1A1A1A]" data-testid="article-render">
      {content.content.map((node, i) => (
        <Fragment key={i}>
          <RenderNode node={node} ctx={ctx} />
        </Fragment>
      ))}
    </div>
  )
}

function RenderNode({ node, ctx }: { node: JSONContent; ctx: EmbedCtx }): ReactNode {
  switch (node.type) {
    case 'fileRef': {
      const id = (node.attrs as { fileId?: string } | undefined)?.fileId
      if (!id) return null
      return <FileCard id={id} meta={ctx.fileMap.get(id) ?? null} loading={ctx.filesLoading} />
    }
    case 'packRef': {
      const id = (node.attrs as { packId?: string } | undefined)?.packId
      if (!id) return null
      return <PackCard id={id} meta={ctx.packMap.get(id) ?? null} loading={ctx.packsLoading} />
    }
    case 'heading': {
      const level = (node.attrs as { level?: number } | undefined)?.level ?? 2
      const cls =
        level <= 2 ? 'mt-4 mb-2 text-[18px] font-bold' : 'mt-3 mb-1.5 text-[16px] font-semibold'
      return <p className={cls}><InlineContent node={node} ctx={ctx} /></p>
    }
    case 'bulletList':
      return (
        <ul className="my-2 list-disc pl-5">
          {(node.content ?? []).map((li, i) => (
            <li key={i}><InlineContent node={li} ctx={ctx} /></li>
          ))}
        </ul>
      )
    case 'orderedList':
      return (
        <ol className="my-2 list-decimal pl-5">
          {(node.content ?? []).map((li, i) => (
            <li key={i}><InlineContent node={li} ctx={ctx} /></li>
          ))}
        </ol>
      )
    case 'blockquote':
      return (
        <blockquote className="my-2 border-l-2 border-[#19caad] pl-3 text-[#4B5563]">
          {(node.content ?? []).map((c, i) => (
            <RenderNode key={i} node={c} ctx={ctx} />
          ))}
        </blockquote>
      )
    case 'paragraph':
      return (
        <p className="my-2 text-[14px] leading-relaxed">
          <InlineContent node={node} ctx={ctx} />
        </p>
      )
    default:
      // 容器类节点(listItem 等)递归;纯文本走 InlineContent
      if (node.content) {
        return (
          <>
            {node.content.map((c, i) => (
              <RenderNode key={i} node={c} ctx={ctx} />
            ))}
          </>
        )
      }
      return <InlineContent node={node} ctx={ctx} />
  }
}

/** 渲染内联内容(text + marks);嵌入节点不会出现在内联层。 */
function InlineContent({ node, ctx }: { node: JSONContent; ctx: EmbedCtx }): ReactNode {
  if (node.type === 'text') {
    let el: ReactNode = node.text ?? ''
    for (const mark of node.marks ?? []) {
      if (mark.type === 'bold') el = <strong>{el}</strong>
      else if (mark.type === 'italic') el = <em>{el}</em>
      else if (mark.type === 'code') el = <code className="rounded bg-[#F1F2F4] px-1">{el}</code>
      else if (mark.type === 'link') {
        const href = (mark.attrs as { href?: string } | undefined)?.href ?? '#'
        el = (
          <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="text-[#2563EB] underline">
            {el}
          </a>
        )
      }
    }
    return <>{el}</>
  }
  return (
    <>
      {(node.content ?? []).map((c, i) => (
        <Fragment key={i}>
          {c.type === 'fileRef' || c.type === 'packRef' ? (
            <RenderNode node={c} ctx={ctx} />
          ) : (
            <InlineContent node={c} ctx={ctx} />
          )}
        </Fragment>
      ))}
    </>
  )
}

/** 嵌入解析进行中:spinner +「资源正在加载中…」(网速慢时不误判失效)。 */
function LoadingEmbed({ testId }: { testId: string }) {
  return (
    <div
      data-testid={testId}
      aria-busy="true"
      className="my-3 flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4"
    >
      <span className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-[#E5E7EB] border-t-[#19caad]" aria-hidden="true" />
      <span className="flex-1 text-[13px] text-[#9CA3AF]">资源正在加载中…</span>
    </div>
  )
}

/** 嵌入失效降级(FHvrD):circle-alert + 「内容已失效」+ 引用 uuid。 */
function FailedEmbed({ kind, id, testId }: { kind: 'fileRef' | 'packRef'; id: string; testId: string }) {
  return (
    <div
      data-testid={testId}
      className="my-2 flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
      </svg>
      <span className="flex-1 text-[13px] text-[#9CA3AF]">内容已失效</span>
      <span className="truncate text-[12px] text-[#D1D5DB]">{kind} · {id.slice(0, 8)}</span>
    </div>
  )
}

/** 文件类型中文标签(fmeta:「图片 · 2.4 MB」)。 */
function fileKindLabel(mime: string | null, type: string): string {
  const m = mime ?? ''
  if (m.startsWith('image/')) return '图片'
  if (m.startsWith('video/')) return '视频'
  if (m.startsWith('audio/')) return '音频'
  if (m === 'application/pdf') return 'PDF'
  return type === 'folder' ? '文件夹' : '文件'
}

/** 时长 mm:ss(≥1h 则 h:mm:ss);videoEmbed 右下角 chip 用。 */
function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const p = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${p(m)}:${p(s)}` : `${p(m)}:${p(s)}`
}

/** 预览区等比缩放比例:有探测宽高用真实比;无 → fallback(图片走 h-auto 自然比可传 undefined)。 */
function aspectRatioOf(meta: PublicFileMeta, fallback?: string): string | undefined {
  if (meta.media_width && meta.media_height) return `${meta.media_width} / ${meta.media_height}`
  return fallback
}

/** 嵌入卡 footer(imageEmbed/videoEmbed 共用):左「描述 · 大小」+ 右绿色「查看」钮(→ /preview/:id;唯一跳转入口)。 */
function EmbedFooter({ to, label, withIcon, chevron }: { to: string; label: string; withIcon?: 'image'; chevron?: boolean }) {
  return (
    <span className="flex items-center justify-between bg-white px-3 py-2.5">
      <span className="flex min-w-0 items-center gap-1.5">
        {withIcon === 'image' && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
          </svg>
        )}
        <span className="truncate text-[12px] text-[#9CA3AF]">{label}</span>
      </span>
      <Link
        to={to}
        className="flex shrink-0 items-center gap-1 rounded-lg bg-[#19caad] px-3 py-1.5 text-[12px] font-semibold text-white no-underline active:opacity-80"
      >
        查看
        {chevron && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m9 18 6-6-6-6" />
          </svg>
        )}
      </Link>
    </span>
  )
}

/** 图片嵌入(设计稿 Rps7o imageEmbed):大图直出(feed 480 档),宽度自适应、高度等比缩放;
 *  点图片不跳转,footer「查看」才进 /preview/:id。 */
function ImageEmbed({ meta, thumb, fmeta }: { meta: PublicFileMeta; thumb: string; fmeta: string }) {
  return (
    <div
      data-testid={`ARTd1-fileref-${meta.id}`}
      className="my-3 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white"
    >
      <img
        src={thumb}
        alt={meta.name}
        className="m-0 block h-auto w-full"
        style={{ aspectRatio: aspectRatioOf(meta) }}
      />
      <EmbedFooter to={`/preview/${meta.id}`} label={`${meta.name} · ${fmeta}`} withIcon="image" chevron />
    </div>
  )
}

/** 视频嵌入(设计稿 ZqEUP videoEmbed):暗色预览区(按真实宽高比等比缩放,无探测数据 fallback 16:9)
 *  + 居中播放钮(点击原地内联播放,不跳转)+ 时长 chip;footer「查看」才进 /preview/:id。 */
function VideoEmbed({ meta, fmeta }: { meta: PublicFileMeta; fmeta: string }) {
  const [playing, setPlaying] = useState(false)
  const streamURL = streamUrlOf(meta.base_file_id, meta.name)
  return (
    <div
      data-testid={`ARTd1-fileref-${meta.id}`}
      className="my-3 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white"
    >
      <div
        className="relative flex w-full items-center justify-center bg-[#1A1A1A]"
        style={{ aspectRatio: aspectRatioOf(meta, '16 / 9') }}
      >
        {playing && streamURL ? (
          <video src={streamURL} controls autoPlay playsInline className="m-0 block h-full w-full bg-black" />
        ) : (
          <>
            {streamURL && (
              <button
                type="button"
                aria-label="播放"
                onClick={() => setPlaying(true)}
                className="flex h-14 w-14 items-center justify-center rounded-full bg-white/80 active:bg-white"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#1A1A1A" stroke="#1A1A1A" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="6 3 20 12 6 21 6 3" />
                </svg>
              </button>
            )}
            {meta.duration_ms != null && (
              <span className="absolute bottom-2.5 right-3 rounded bg-black/60 px-1.5 py-0.5 text-[11px] text-white">
                {formatDuration(meta.duration_ms)}
              </span>
            )}
          </>
        )}
      </div>
      <EmbedFooter to={`/preview/${meta.id}`} label={fmeta} />
    </div>
  )
}

function FileCard({ id, meta, loading }: { id: string; meta: PublicFileMeta | null; loading: boolean }) {
  if (!meta) {
    return loading
      ? <LoadingEmbed testId={`ARTd1-fileref-${id}`} />
      : <FailedEmbed kind="fileRef" id={id} testId={`ARTd1-fileref-${id}`} />
  }

  const mime = meta.mime_type ?? ''
  const isImage = mime.startsWith('image/')
  const isVideo = mime.startsWith('video/')
  const thumb = thumbUrl(meta.thumb_url_list, meta.base_file_id, meta.name)
  const fmeta = [fileKindLabel(meta.mime_type, meta.type), meta.size_original ? formatBytes(meta.size_original) : null]
    .filter(Boolean)
    .join(' · ')

  // 图片/视频 → 大卡嵌入(Rps7o/ZqEUP);音频/其他文件维持小文件卡(暂不变)。
  const feedThumb = thumbUrl(meta.thumb_url_feed, meta.base_file_id, meta.name)
  if (isImage && feedThumb) return <ImageEmbed meta={meta} thumb={feedThumb} fmeta={fmeta} />
  if (isVideo) return <VideoEmbed meta={meta} fmeta={fmeta} />

  return (
    <Link
      to={`/preview/${meta.id}`}
      data-testid={`ARTd1-fileref-${meta.id}`}
      className="my-3 flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 no-underline active:bg-[#F6F7F8]"
    >
      {isImage && thumb ? (
        <img src={thumb} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
      ) : (
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-[#FEF3C7] text-[#F59E0B]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-[#1A1A1A]">{meta.name}</p>
        <p className="mt-1 text-[11px] text-[#9CA3AF]">{fmeta}</p>
      </div>
      <span className="shrink-0 rounded-lg bg-[#F0FBF9] px-3 py-1.5 text-[12px] text-[#19caad]">查看</span>
    </Link>
  )
}

function PackCard({ id, meta, loading }: { id: string; meta: PublicPackMeta | null; loading: boolean }) {
  if (!meta) {
    return loading
      ? <LoadingEmbed testId={`ARTd1-packref-${id}`} />
      : <FailedEmbed kind="packRef" id={id} testId={`ARTd1-packref-${id}`} />
  }

  const thumb = thumbUrl(meta.cover_thumb_url_feed, meta.cover_base_file_id, meta.cover_name ?? '')
  const sub = [meta.item_count != null ? `${meta.item_count} 个文件` : null].filter(Boolean).join(' · ')
  const price = meta.price_coin ? `${meta.price_coin} COIN` : '免费'
  return (
    <Link
      to={`/pack/${meta.id}`}
      data-testid={`ARTd1-packref-${meta.id}`}
      className="my-3 flex items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-3 no-underline active:bg-[#F6F7F8]"
    >
      {thumb ? (
        <img src={thumb} alt="" className="h-16 w-16 shrink-0 rounded-[10px] object-cover" />
      ) : (
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[10px] bg-[#E0E7FF] text-[#4F46E5]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
          </svg>
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-semibold text-[#1A1A1A]">{meta.name}</p>
        {sub && <p className="mt-1.5 text-[12px] text-[#9CA3AF]">{sub}</p>}
        <p className="mt-1 text-[13px] font-semibold text-[#19caad]">{price}</p>
      </div>
      <span className="shrink-0 self-center rounded-[10px] bg-[#19caad] px-3.5 py-2 text-[13px] font-semibold text-white">查看</span>
    </Link>
  )
}
